import { type ReactNode, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { checkPermission } from '@/lib/auth/permissions'
import { type PermissionCheck } from '@/models/domain/permissions'
import { useAuthStore } from '@/stores/auth-store'

interface ProtectedRouteProps {
  children: ReactNode
  permissions?: PermissionCheck
  redirectTo?: string
}

export function ProtectedRoute({
  children,
  permissions,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const location = useLocation()
  const { isAuthenticated, user, isLoading, loadUser } = useAuthStore()

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      void loadUser()
    }
  }, [isAuthenticated, isLoading, loadUser])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  if (permissions) {
    const permissionResult = checkPermission(user, permissions)

    if (!permissionResult.allowed) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {permissionResult.reason || 'Insufficient permissions'}
            </p>
          </div>
        </div>
      )
    }
  }

  return <>{children}</>
}
